<template>
    <SettingsCard id="block-lunch" title="Обеденные перерывы" class="ds-settings-block">
        <template v-if="editor.lunchesReady.value" #actions>
            <span class="ds-settings-muted">Правил {{ rows.length }} из {{ LUNCH_RULES_LIMIT }}</span>
            <button
                type="button"
                class="ds-glass-btn ds-glass-btn-secondary ds-settings-add-button"
                :disabled="!editor.canAddLunch.value"
                :title="rows.length >= LUNCH_RULES_LIMIT ? `Не больше ${LUNCH_RULES_LIMIT} правил` : undefined"
                @click="add"
            >
                <SettingsIcon name="plus" :size="14" :stroke-width="2" />
                Добавить правило
            </button>
        </template>

        <SettingsBlockState
            :status="editor.load.lunches"
            :rows="3"
            error-text="Не удалось загрузить правила обеденных перерывов."
            @retry="editor.loadLunches()"
        >
            <div v-if="!rows.length" class="ds-settings-empty">
                Правил пока нет. Добавьте первое правило.
            </div>
            <template v-else>
                <!-- Шкала отражает черновик; хвост — условная доля без новой максимальной длительности смены. -->
                <div class="ds-settings-lunch-scale-wrap" aria-hidden="true">
                    <div class="ds-settings-lunch-scale">
                        <div
                            v-for="(segment, index) in segments"
                            :key="segment.key"
                            class="ds-settings-lunch-segment"
                            :class="index % 2 ? 'is-dense' : 'is-soft'"
                            :style="{ flexGrow: segment.size }"
                        >{{ segment.label }}</div>
                        <div class="ds-settings-lunch-segment is-tail" :style="{ flexGrow: tailSize }">без перерыва</div>
                    </div>
                </div>

                <div class="ds-settings-lunch-table" role="table" aria-label="Правила обеденных перерывов">
                    <div class="ds-settings-lunch-row ds-settings-param-head" role="row">
                        <span role="columnheader">#</span>
                        <span role="columnheader">Смена, от</span>
                        <span role="columnheader">До</span>
                        <span role="columnheader">Перерыв</span>
                        <span role="columnheader"><span class="sr-only">Действия</span></span>
                    </div>
                    <div
                        v-for="(row, index) in rows"
                        :id="`lunch-row-${row.key}`"
                        :key="row.key"
                        class="ds-settings-lunch-row"
                        :class="{
                            'is-changed': editor.changedAnchors.value.has(`lunch-row-${row.key}`),
                            'is-highlighted': editor.highlightedAnchor.value === `lunch-row-${row.key}`,
                        }"
                        role="row"
                    >
                        <span class="ds-settings-muted" role="cell">{{ index + 1 }}</span>
                        <span role="cell">{{ fromOf(index) }} мин</span>
                        <span role="cell">
                            <SettingsNumberField
                                :model-value="row.to"
                                :label="`Правило ${index + 1}: до, минут`"
                                unit="мин"
                                :changed="isFieldChanged(row, 'to')"
                                :invalid="Boolean(editor.lunchErrors.value[row.key]?.to)"
                                :error-text="editor.lunchErrors.value[row.key]?.to"
                                :disabled="editor.saving.value"
                                @update:model-value="editor.updateLunchRow(row.key, 'to', $event)"
                                @blur="editor.sortLunchRows()"
                            />
                        </span>
                        <span role="cell">
                            <SettingsNumberField
                                :model-value="row.brk"
                                :label="`Правило ${index + 1}: перерыв, минут`"
                                unit="мин"
                                :changed="isFieldChanged(row, 'brk')"
                                :invalid="Boolean(editor.lunchErrors.value[row.key]?.brk)"
                                :error-text="editor.lunchErrors.value[row.key]?.brk"
                                :disabled="editor.saving.value"
                                @update:model-value="editor.updateLunchRow(row.key, 'brk', $event)"
                            />
                        </span>
                        <span class="ds-settings-lunch-actions" role="cell">
                            <span v-if="rowError(row.key)" class="ds-settings-field-error">{{ rowError(row.key) }}</span>
                            <button
                                type="button"
                                class="ds-settings-icon-button"
                                :disabled="editor.saving.value"
                                :aria-label="`Удалить правило ${index + 1}`"
                                title="Удалить правило"
                                @click="editor.removeLunchRow(row.key)"
                            >
                                <SettingsIcon name="trash" :size="16" />
                            </button>
                        </span>
                    </div>
                </div>
            </template>

            <p v-if="rows.length > LUNCH_RULES_LIMIT" class="ds-settings-card-note ds-settings-card-note-warning">
                Правил больше {{ LUNCH_RULES_LIMIT }}: добавление недоступно. Существующие правила можно изменить или удалить.
            </p>
            <p class="ds-settings-card-note">«От» подставляется из предыдущего правила. При {{ LUNCH_RULES_LIMIT }} правилах кнопка «Добавить» неактивна.</p>
        </SettingsBlockState>
    </SettingsCard>
</template>

<script setup lang="ts">
import { computed, nextTick } from 'vue'
import SettingsCard from '@/ui/SettingsCard.vue'
import SettingsBlockState from './SettingsBlockState.vue'
import SettingsIcon from './SettingsIcon.vue'
import SettingsNumberField from './SettingsNumberField.vue'
import { useSettingsEditor } from './useSettingsEditor'
import { LUNCH_RULES_LIMIT, lunchToMinutes, parseLunchMinutes, type LunchDraftRow } from './settingsModel'
import { useSettingsStore } from '@/stores/settingsStore'

const editor = useSettingsEditor()
const settingsStore = useSettingsStore()

const rows = computed(() => editor.lunchDraft.value.rows)

/** «От» — граница предыдущей корректной строки (для первой — 0). */
const fromValues = computed(() => {
    let previous = 0
    return rows.value.map((row) => {
        const from = previous
        const to = parseLunchMinutes(row.to)
        if (to !== null && to > previous) previous = to
        return from
    })
})
const fromOf = (index: number) => fromValues.value[index] ?? 0

const segments = computed(() => rows.value.flatMap((row, index) => {
    const to = parseLunchMinutes(row.to)
    const from = fromOf(index)
    if (to === null || to <= from) return []
    const brk = parseLunchMinutes(row.brk)
    return [{ key: row.key, size: to - from, label: `${from}–${to} · ${brk ?? '?'} мин` }]
}))

const tailSize = computed(() => {
    const total = segments.value.reduce((sum, segment) => sum + segment.size, 0)
    return total ? Math.max(total * 0.16, 1) : 1
})

function isFieldChanged(row: LunchDraftRow, field: 'to' | 'brk') {
    if (row.id === null) return true
    const rule = settingsStore.lunchSettings.find((item) => item.id === row.id)
    if (!rule) return false
    const baseline = lunchToMinutes(field === 'to' ? rule.shift_duration_seconds : rule.break_duration_seconds)
    return parseLunchMinutes(field === 'to' ? row.to : row.brk) !== baseline
}

const rowError = (key: string) => {
    const errors = editor.lunchErrors.value[key]
    return errors?.to ?? errors?.brk ?? ''
}

async function add() {
    editor.addLunchRow()
    await nextTick()
    const last = rows.value[rows.value.length - 1]
    if (last) await editor.revealAnchor(`lunch-row-${last.key}`)
}
</script>
